import React from 'react';
import { FileText, Clock, Check, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RecentForms = ({ applications = [] }) => {
  const navigate = useNavigate();

  // Helper function to get employee name from form_data
  const getEmployeeName = (formData) => {
    return formData?.employeeName || 
           `${formData?.firstName || ''} ${formData?.lastName || ''}`.trim() || 
           'Unknown Applicant';
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    switch(status) {
      case 'draft':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          icon: Clock,
          label: 'Pending'
        };
      case 'hr_reviewed':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-800',
          icon: Eye,
          label: 'HR Reviewed'
        };
      case 'finalized':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          icon: Check,
          label: 'Finalized'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          icon: FileText,
          label: status || 'Unknown'
        };
    }
  };

  // Take only the 5 most recent applications
  const recentApplications = applications
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  if (recentApplications.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          Recent Forms
        </h3>
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No recent forms available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-slate-800">
          Recent Forms
        </h3>
        <span className="text-sm text-gray-500">
          Last 5 applications
        </span>
      </div>

      <div className="space-y-3">
        {recentApplications.map((app) => {
          const StatusIcon = getStatusBadge(app.status).icon;
          const statusStyle = getStatusBadge(app.status);
          
          return (
            <div
              key={app.id}
              onClick={() => navigate(`/hr/form/${app.slug_token}`)}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition group"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition">
                  <FileText className="text-blue-600 w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">
                    {getEmployeeName(app.form_data)}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span>{formatDate(app.created_at)}</span>
                    <span>•</span>
                    <span>{app.form_data?.positionDesired || 'Position not specified'}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                  <StatusIcon size={12} />
                  {statusStyle.label}
                </span>
                <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition" />
              </div>
            </div>
          );
        })}
      </div>

      {applications.length > 5 && (
        <div className="mt-4 text-center">
          <button 
            onClick={() => navigate('/hr/form')}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View all {applications.length} applications
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentForms;